import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../common/SEOHead';
import { getGuidesHubSEO } from '../../utils/seo';
import { useInView } from '../../hooks/useInView';
import { GUIDE_CATEGORIES, getAllGuides, getGuidesByCategory } from '../../data/guides';

const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className = '',
}) => {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Guides: React.FC = () => {
  const allGuides = getAllGuides();
  const latest = allGuides.slice(0, 6);

  return (
    <div className="bg-cream-50">
      <SEOHead seoData={getGuidesHubSEO()} />

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden border-b border-warm-200">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[50%] h-[100%] bg-gradient-to-bl from-forest-100/50 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[40%] h-[80%] bg-gradient-to-tr from-gold-100/40 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative section-container py-24 sm:py-32 lg:py-40">
          <div className="max-w-4xl">
            <span
              className="text-nano font-semibold text-gold-700 uppercase tracking-[0.25em] mb-8 block animate-fade-up"
              style={{ animationDelay: '0.1s', opacity: 0 }}
            >
              — Guides —
            </span>

            <h1
              className="font-display italic text-display-lg sm:text-display-xl lg:text-display-2xl text-forest-900 tracking-tight leading-[1.0] mb-10 animate-fade-up"
              style={{ animationDelay: '0.2s', opacity: 0 }}
            >
              The things your<br />telehealth ad<br />won&rsquo;t tell you.
            </h1>

            <p
              className="text-body-lg sm:text-xl text-warm-600 max-w-2xl leading-relaxed animate-fade-up"
              style={{ animationDelay: '0.3s', opacity: 0 }}
            >
              Plain-language guides on the medications we route &mdash; sildenafil,
              tadalafil, finasteride, semaglutide, isotretinoin. What the trial
              data actually says. Where the markup hides. What to check before
              you buy from anyone, including us.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Categories ===== */}
      <section className="py-20 sm:py-28">
        <div className="section-container">
          <div className="max-w-3xl mb-14">
            <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-4 block">
              By Category
            </span>
            <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight leading-tight">
              Pick the one that hurts.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {GUIDE_CATEGORIES.map((cat, idx) => {
              const count = getGuidesByCategory(cat.id).length;
              return (
                <Reveal key={cat.id} delay={idx * 70}>
                  <Link
                    to={`/guides/${cat.id}`}
                    className="group block bg-cream-100 rounded-2xl border border-warm-200 p-8 sm:p-10 hover:shadow-hover hover:-translate-y-0.5 hover:border-gold-400/50 transition-all duration-500 ease-spring h-full"
                  >
                    <div className="flex items-start justify-between mb-5">
                      <span className="text-[10px] font-semibold text-gold-700 uppercase tracking-[0.2em]">
                        {cat.eyebrow}
                      </span>
                      <span className="font-accent italic text-xs text-warm-500">
                        {count} {count === 1 ? 'guide' : 'guides'}
                      </span>
                    </div>
                    <h3 className="font-display text-heading-lg sm:text-[28px] text-forest-900 tracking-tight leading-tight mb-3 group-hover:text-gold-800 transition-colors">
                      {cat.label}
                    </h3>
                    <p className="font-accent italic text-body text-gold-700 mb-4 leading-relaxed">
                      {cat.tagline}
                    </p>
                    <p className="text-body-sm text-warm-600 leading-relaxed">
                      {cat.pillarHook}
                    </p>
                    <div className="mt-7 flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-forest-700 font-semibold">
                      Read guides
                      <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Latest ===== */}
      <section className="bg-cream-100/60 py-20 sm:py-28 border-t border-warm-200">
        <div className="section-container">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div className="max-w-2xl">
              <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-4 block">
                Latest
              </span>
              <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight leading-tight">
                Recently published.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {latest.map((g, idx) => {
              const cat = GUIDE_CATEGORIES.find(c => c.id === g.category);
              return (
                <Reveal key={g.slug} delay={idx * 50}>
                  <Link
                    to={`/guides/${g.category}/${g.slug}`}
                    className="group block h-full bg-cream-50 rounded-2xl border border-warm-200 p-7 hover:shadow-hover hover:-translate-y-0.5 hover:border-gold-400/50 transition-all duration-500 ease-spring"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-[10px] font-semibold text-gold-700 uppercase tracking-[0.2em]">
                        {cat?.label || g.category}
                      </span>
                      <span className="text-warm-300">·</span>
                      <span className="font-accent italic text-[11px] text-warm-500">
                        {g.readMinutes} min read
                      </span>
                    </div>
                    <h3 className="font-display text-[20px] text-forest-900 leading-snug tracking-tight mb-3 group-hover:text-gold-800 transition-colors">
                      {g.title}
                    </h3>
                    <p className="text-[14px] text-warm-600 leading-[1.65] line-clamp-3">
                      {g.excerpt}
                    </p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Note on bias ===== */}
      <section className="border-t border-warm-200">
        <div className="section-container py-20 sm:py-28">
          <Reveal className="max-w-2xl mx-auto text-center">
            <span className="text-nano font-semibold text-gold-700 uppercase tracking-[0.25em] mb-6 block">
              — A note on what this is —
            </span>
            <h2 className="font-display italic text-display-sm sm:text-[36px] text-forest-900 tracking-tight leading-tight mb-7">
              We sell some of these.
            </h2>
            <p className="text-body-lg text-warm-600 leading-relaxed mb-5">
              LiberaCure routes orders to licensed personal-import pharmacies.
              These guides are written by the same people running that business,
              so we have a financial reason to want you to read them and stick around.
            </p>
            <p className="text-body text-warm-600 leading-relaxed mb-10">
              Read them with that in mind. Each guide names what we sell and what
              we don&rsquo;t at the bottom. The protocols are what we&rsquo;d tell a
              friend &mdash; not what maximizes reorder rate.
            </p>
            <Link to="/honestly" className="btn-secondary">
              Why we look like a scam (we explain)
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Guides;
